import React, {
    createContext,
    useContext,
    useEffect,
    useState
} from "react";
import { useUser } from "./UserContext";
import { clearChat, continueChat, startChat } from "../services/ChatBotService";
import { ChatMessage, ChatMessage_Author, chatMessage_AuthorFromJSON, ChatResponse, ToolCall } from "build-ts/chat";
import { useAnalysisDrawerContext } from "./AnalysisDrawerContext";
import { AnalysisTreeMap, NodeInsights } from "../containers/Interfaces";
import { AnalysisTreeSessionKey } from "build-ts/stateful_api";
import { AgentType, ChatRequest, ClearChatRequest, TISAInput } from "build-ts/agent";

interface Message {
    text: string;
    sender: "user" | "bot";
    toolCalls: ToolCall[];
    execution_id?: string;
}

interface ChatContextType {
    messages: Message[];
    trees: string[];
    setTrees: React.Dispatch<React.SetStateAction<string[]>>;
    metadataId: string;
    setMetadataId: React.Dispatch<React.SetStateAction<string>>;
    input: string;
    isLoading: boolean;
    transactionId: string;
    setTransactionId: React.Dispatch<React.SetStateAction<string>>;
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
    setInput: React.Dispatch<React.SetStateAction<string>>;
    handleNewChat: () => Promise<void>;
    handleContinueTisaChat: (userMessages: string[]) => Promise<void>;
    handleLoadChatHistory: (metadataId: string) => Promise<string | undefined>;
    selectedNode: NodeInsights | null;
    setSelectedNode: React.Dispatch<React.SetStateAction<NodeInsights | null>>;
    referencedNodeId: {nodeId: string | null; treeId: string | null};
    setReferencedNodeId: React.Dispatch<React.SetStateAction<{nodeId: string | null; treeId: string | null}>>;

    // for custom agent
    setUserId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const ChatBotContext = createContext<ChatContextType | undefined>(undefined);

export const ChatBotContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { userId: user } = useUser();
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [chatId, setChatId] = useState<string | null>(null);
    const { responseData } = useAnalysisDrawerContext();
    const [trees, setTrees] = useState<string[]>([]);
    const [metadataId, setMetadataId] = useState<string>("");
    const [transactionId, setTransactionId] = useState<string>("");
    const [selectedNode, setSelectedNode] = useState<NodeInsights | null>(null);
    const [userId, setUserId] = useState<string | undefined>(user?.toString());
    const [referencedNodeId, setReferencedNodeId] = useState<{nodeId: string | null; treeId: string | null}>({nodeId: null, treeId: null});

    // Function to display history when chat is initialized
    const displayHistory = (chatHistory: ChatMessage[]): Message[] => {
        const convertedMessages: Message[] = [];

        for (let i = 0; i < chatHistory.length; i++) {
            const msg = chatHistory[i];
            if (chatMessage_AuthorFromJSON(msg.author) === (ChatMessage_Author.HUMAN) && msg.content) {
                convertedMessages.push({
                    text: msg.content,
                    sender: "user",
                    toolCalls: [],
                });
            }
            else if (chatMessage_AuthorFromJSON(msg.author) === (ChatMessage_Author.AI) && msg.content) {
                convertedMessages.push({
                    text: msg.content,
                    sender: "bot",
                    toolCalls: [],
                });
            }
        }
        return convertedMessages;
    };

    // Function to transform client tree sessions
    const transformClientTreeSessions = (responseData: { [key: string]: AnalysisTreeMap }): AnalysisTreeSessionKey[] => {
        const treeSessions: AnalysisTreeSessionKey[] = [];
        
        // Iterate through each transaction in responseData
        Object.entries(responseData).forEach(([transactionId, treeMap]) => {
            if (treeMap.tree?.header?.id) {
                treeSessions.push(
                    AnalysisTreeSessionKey.create({
                        tree_id: treeMap.tree.header.id,
                        state_key: {
                            transaction_id: transactionId
                        }
                    })
                );
            }
        });

        return treeSessions;
    };

    const handleLoadChatHistory = async (metadataId: string): Promise<string | undefined> => {
        try{
            const chatRequest = ChatRequest.create({
                agent_type: AgentType.TISA_CHAT,
                tisa_input: TISAInput.create({
                    user_id: userId?.toString(),
                    metadata_id: metadataId,
                    // No Agent FSM invocation in this case
                })
            });
            
            const chatResponse: ChatResponse = await startChat(chatRequest);
            
            if(chatResponse.chat_id){
                setChatId(chatResponse.chat_id);
            }

            // set chat history
            if(chatResponse.messages && chatResponse.messages.length > 0){
                const convertedMessages = displayHistory(chatResponse.messages);

                if(convertedMessages.length > 0){
                    setMessages(convertedMessages);
                } else {
                    setMessages([]);
                }
            } else {
                setMessages([]);
            }
            return chatResponse.chat_id;
        } catch (error) {
            console.error("Error loading chat history:", error);
        } 
    };

    const handleContinueTisaChat = async (userMessages: string[]): Promise<void> => {
        if(chatId){
            const treeSessions = transformClientTreeSessions(responseData);
            
            // Add user message
            const userMsg = { text: userMessages[0], sender: "user" as const, toolCalls: [] };
            setMessages(prev => [...prev, userMsg]);

            setIsLoading(true);

            // Initialize empty bot message
            const initialBotMessage = { 
                text: "", 
                sender: "bot" as const, 
                toolCalls: [] 
            };
            setMessages(prev => [...prev, initialBotMessage]);

            const chatRequest = ChatRequest.create({
                user_messages: userMessages,
                stream: true,
                agent_type: AgentType.TISA_CHAT,
                tisa_input: TISAInput.create({
                    chat_id: chatId,
                    user_id: userId?.toString(),
                    metadata_id: metadataId,
                    tree_sessions: treeSessions,
                }),
                client_tag: "FRIDA",
            });

            try {
                const response = await continueChat(chatRequest);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                if (!response.body) {
                    throw new Error("No response body received");
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                
                let currentContent = "";
                let currentToolCalls: ToolCall[] = [];
                

                const updateBotMessage = () => {
                    setMessages(prev => [
                        ...prev.slice(0, -1),
                        {
                            text: currentContent,
                            sender: "bot",
                            toolCalls: currentToolCalls
                        }
                    ]);
                };

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });
                    const lines = chunk.split("\n");

                    for (const line of lines) {
                        if (line.startsWith("data: ")) {
                            try {
                                const jsonStr = line.slice(6).trim(); // Remove 'data: ' prefix
                                if (jsonStr === "[DONE]") continue;
                                
                                const chatResponse = ChatResponse.fromJSON(JSON.parse(jsonStr));
                                
                                if (chatResponse.messages && chatResponse.messages.length > 0) {
                                    const aiMessage = chatResponse.messages.find(msg => msg.author === ChatMessage_Author.AI);
                                    
                                    if (aiMessage) {
                                        if (aiMessage.content) {
                                            currentContent = aiMessage.content;
                                        }
                                        if (aiMessage.tool_calls && aiMessage.tool_calls.length > 0) {
                                            currentToolCalls = [
                                                ...currentToolCalls,
                                                ...aiMessage.tool_calls
                                            ];
                                        }
                                        updateBotMessage();
                                    }
                                }
                            } catch (e) {
                                console.error("Error parsing ChatResponse:", e);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Error in chat continuation:", error);
            } finally {
                setIsLoading(false);
            }
        }
        else {
            const treeSessions = transformClientTreeSessions(responseData);
            
            // Add user message
            const userMsg = { text: userMessages[0], sender: "user" as const, toolCalls: [] };
            setMessages(prev => [...prev, userMsg]);

            setIsLoading(true);

            // Initialize empty bot message
            const initialBotMessage = { 
                text: "", 
                sender: "bot" as const, 
                toolCalls: [] 
            };
            setMessages(prev => [...prev, initialBotMessage]);

            const chatRequest = ChatRequest.create({
                user_messages: userMessages,
                stream: true,
                agent_type: AgentType.TISA_CHAT,
                tisa_input: TISAInput.create({
                    user_id: userId?.toString(),
                    tree_sessions: treeSessions,
                }),
                client_tag: "FRIDA"
            });

            try {
                const response = await continueChat(chatRequest);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                if (!response.body) {
                    throw new Error("No response body received");
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                
                let currentContent = "";
                let currentToolCalls: ToolCall[] = [];
                

                const updateBotMessage = () => {
                    setMessages(prev => [
                        ...prev.slice(0, -1),
                        {
                            text: currentContent,
                            sender: "bot",
                            toolCalls: currentToolCalls
                        }
                    ]);
                };

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });
                    const lines = chunk.split("\n");

                    for (const line of lines) {
                        if (line.startsWith("data: ")) {
                            try {
                                const jsonStr = line.slice(6).trim(); // Remove 'data: ' prefix
                                if (jsonStr === "[DONE]") continue;
                                
                                const chatResponse = ChatResponse.fromJSON(JSON.parse(jsonStr));

                                if(chatResponse.chat_id){
                                    setChatId(chatResponse.chat_id);
                                }
                                
                                if (chatResponse.messages && chatResponse.messages.length > 0) {
                                    const aiMessage = chatResponse.messages.find(msg => msg.author === ChatMessage_Author.AI);
                                    
                                    if (aiMessage) {
                                        if (aiMessage.content) {
                                            currentContent = aiMessage.content;
                                        }
                                        if (aiMessage.tool_calls && aiMessage.tool_calls.length > 0) {
                                            currentToolCalls = [
                                                ...currentToolCalls,
                                                ...aiMessage.tool_calls
                                            ];
                                        }
                                        updateBotMessage();
                                    }
                                }
                            } catch (e) {
                                console.error("Error parsing ChatResponse:", e);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Error in chat continuation:", error);
            } finally {
                setIsLoading(false);
            }
        }
    };


    const handleNewChat = async (): Promise<void> => {
        setMessages([]);
        setInput("");

        // Clear chat history
        if(chatId){
            const request = ClearChatRequest.create({
                chat_id: chatId,
            });
            await clearChat(request);
        }
        setChatId(null);

        // Start new chat
        const chatRequest = ChatRequest.create({
            agent_type: AgentType.TISA_CHAT,
            tisa_input: TISAInput.create({
                user_id: userId?.toString(),
                metadata_id: metadataId,
                tree_sessions: transformClientTreeSessions(responseData)
            }),
            client_tag: "FRIDA",
        });
        
        const chatResponse: ChatResponse = await startChat(chatRequest);
        
        if(chatResponse?.chat_id){
            setChatId(chatResponse?.chat_id);
        }
    };

    useEffect(() => {
        if(metadataId){
            handleLoadChatHistory(metadataId);
        }
    }, [metadataId]);

  
    return (
        <ChatBotContext.Provider 
            value={{ 
                messages, 
                input,
                trees,
                setTrees,
                isLoading,
                setMessages,
                setInput,
                handleContinueTisaChat,
                handleLoadChatHistory,
                handleNewChat,
                metadataId,
                setMetadataId,
                transactionId,
                setTransactionId,
                selectedNode,
                setSelectedNode,
                referencedNodeId,
                setReferencedNodeId,
                setUserId
            }}
        >
            {children}
        </ChatBotContext.Provider>
    );
};

export const useChat = () => {
    const context = useContext(ChatBotContext);

    if (!context) {
        throw new Error("useChat must be used within a ChatBotContextProvider");
    }
    return context;
};